
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Loading from 'vue-loading-overlay';
import { APP_CONFIG } from '@/config/app-config';
import cartServices from '@/services/cart-services';
import commonService from '@/services/common-services';
import u from '@/utility/utility';
@Component({
    components: {
        Loading
    },
})
export default class DetailProduct extends Vue {
    @Prop({ type: String, default: '' }) readonly itemUrl!: string;
    private isLoading = false;
    private selectedProduct: any = null;
    private selectedQuantity: number = 1;
    private addPrice: number = 0;
    private mainImage: string = '';
    private itemUrlLocal: string = '';
    private reactivityCounter: number = 0;
    get rate() {
        return this.$store.state[APP_CONFIG.stateKey.commonStore].rate;
    }

    get productPlatform(): 'TAOBAO' | 'TMALL' | '1688' | null {
        if (!this.selectedProduct) {
            return null;
        }

        if (this.selectedProduct.platform === '1688') {
            return '1688';
        } else if (this.selectedProduct.platform === 'taobao') {
            return 'TAOBAO';
        } else if (this.selectedProduct.platform === 'tmall') {
            return 'TMALL';
        }
        
        return null;
    }

    protected proxyImage(src: string): string {
        return u.proxyImage(src);
    }

    get proxiedMainImage(): string {
        return this.proxyImage(this.mainImage);
    }

    get proxiedDescription(): string {
        if (!this.selectedProduct || !this.selectedProduct.description) {
            return '';
        }
        
        // Replace all img src URLs with proxied versions
        return this.selectedProduct.description.replace(
            /(<img[^>]+src=")[^"]+(")/g,
            (match, prefix, suffix) => {
                // Extract the original src URL
                const srcMatch = match.match(/src="([^"]+)"/);
                if (srcMatch && srcMatch[1]) {
                    const originalUrl = srcMatch[1];
                    const proxiedUrl = this.proxyImage(originalUrl);
                    return `${prefix}${proxiedUrl}${suffix}`;
                }
                return match;
            }
        );
    }

    // Computed property for available options from SKUs
    get availableOptions() {
        if (!this.selectedProduct || !this.selectedProduct.skus) {
            return [];
        }

        const optionsMap: { [key: string]: any } = {};

        // Extract all unique options from SKUs
        this.selectedProduct.skus.forEach(sku => {
            if (sku.options) {
                sku.options.forEach(option => {
                    if (!optionsMap[option.name]) {
                        optionsMap[option.name] = {
                            name: option.name,
                            nameEn: option.nameEn,
                            values: []
                        };
                    }

                    // Check if this value already exists
                    const existingValue = optionsMap[option.name].values.find(v => 
                        v.value === option.value
                    );

                    if (!existingValue) {
                        optionsMap[option.name].values.push({
                            value: option.value,
                            valueEn: option.valueEn,
                            isActive: false
                        });
                    }
                });
            }
        });

        return Object.values(optionsMap);
    }

    // Computed property để track selected options cho reactivity
    get selectedOptions() {
        // Force dependency on reactivityCounter to ensure updates
        const counter = this.reactivityCounter;
        
        const selected: { [key: string]: string } = {};
        
        
        // Force reactivity by accessing each value's isActive property explicitly
        this.availableOptions.forEach(optionGroup => {
            
            // Explicitly access each value's isActive to ensure Vue tracks the dependencies
            optionGroup.values.forEach(value => {
                const isActive = value.isActive; // Force Vue to track this dependency
                if (isActive) {
                    selected[optionGroup.name] = value.value;
                }
            });
        });
        
        return selected;
    }

    private getSelectedSkuBasedOnOptions() {
        if (!this.selectedProduct || !this.selectedProduct.skus) {
            return null;
        }

        // Use computed selectedOptions for better reactivity
        const selectedOptions = this.selectedOptions;
        
        // Debug log

        // If no options selected, return first SKU
        if (Object.keys(selectedOptions).length === 0) {
            return this.selectedProduct.skus[0];
        }

        // Find SKU that matches selected options (relaxed matching)
        const matchingSku = this.selectedProduct.skus.find(sku => {
            if (!sku.options || sku.options.length === 0) {
                return false;
            }
            
            
            // Check if all selected options match this SKU
            let allMatch = true;
            for (const [optionName, optionValue] of Object.entries(selectedOptions)) {
                const skuOption = sku.options.find(opt => opt.name === optionName && opt.value === optionValue);
                if (!skuOption) {
                    allMatch = false;
                    break;
                }
            }
            
            return allMatch;
        });

        return matchingSku || this.selectedProduct.skus[0];
    }

    get currentPrice() {
        if (!this.selectedProduct) {
            return 0;
        }


        // For 1688 products with volume pricing
        if (this.selectedProduct.quote_type === 'by_volume' && this.selectedProduct.price_range) {
            const volumePrice = this.getPriceByQuantity1688(this.selectedQuantity);
            return volumePrice + this.addPrice;
        }

        // For products with SKU-based pricing
        if (this.selectedProduct.quote_type === 'by_sku') {
            const selectedSku = this.getSelectedSkuBasedOnOptions();
            if (selectedSku) {
                const skuPrice = selectedSku.promotion_price || selectedSku.price;
                return skuPrice + this.addPrice;
            }
            
            // If no SKU selected but has SKUs, use first available SKU price as default
            if (this.selectedProduct.skus && this.selectedProduct.skus.length > 0) {
                const firstSku = this.selectedProduct.skus[0];
                const firstSkuPrice = firstSku.promotion_price || firstSku.price;
                return firstSkuPrice + this.addPrice;
            }
        }

        // Fallback to base price
        const fallbackPrice = this.selectedProduct.promotion_price || this.selectedProduct.price || 0;
        return fallbackPrice + this.addPrice;
    }

    // Updated function for 1688 price calculation
    private getPriceByQuantity1688(quantity: number): number {
        if (!this.selectedProduct.price_range || this.selectedProduct.price_range.length === 0) {
            return this.selectedProduct.promotion_price || this.selectedProduct.price || 0;
        }

        const priceRanges = this.selectedProduct.price_range;
        // Sort by moq descending to find the appropriate price tier
        const sortedRanges = [...priceRanges].sort((a, b) => b.moq - a.moq);
        
        // Find the appropriate price tier
        for (const range of sortedRanges) {
            if (quantity >= range.moq) {
                return range.promotion_price || range.price;
            }
        }
        
        // If no tier matches, return the lowest tier price
        const ret = priceRanges[0].promotion_price || priceRanges[0].price;
        return ret + this.addPrice;
    }

    // Computed property for price range display
    get priceRangeDisplay(): string {
        if (!this.selectedProduct.price_range || this.selectedProduct.price_range.length === 0) {
            return '';
        }
        
        // Sort by moq ascending for display
        const sortedRanges = [...this.selectedProduct.price_range].sort((a, b) => a.moq - b.moq);
        
        return sortedRanges.map(range => {
            const price = range.promotion_price || range.price;
            return `≥${range.moq}cái: ${price}¥`;
        }).join(' | ');
    }

    get currentPriceVND() {
        return (this.currentPrice * this.rate).toLocaleString('vi-VN');
    }
    get minPrice() {
        if (this.selectedProduct && this.selectedProduct.skus && this.selectedProduct.skus.length > 0) {
            const prices = this.selectedProduct.skus.map(sku => 
                sku.promotion_price || sku.price
            ).filter(p => !isNaN(p) && p > 0);
            return prices.length > 0 ? Math.min(...prices) : (this.selectedProduct.promotion_price || this.selectedProduct.price || 0);
        }
        const ret = (this.selectedProduct && this.selectedProduct.promotion_price) || (this.selectedProduct && this.selectedProduct.price) || 0;
        return ret + this.addPrice;
    }

    get maxPrice() {
        if (this.selectedProduct && this.selectedProduct.skus && this.selectedProduct.skus.length > 0) {
            const prices = this.selectedProduct.skus.map(sku => 
                sku.promotion_price || sku.price
            ).filter(p => !isNaN(p) && p > 0);
            return prices.length > 0 ? Math.max(...prices) : (this.selectedProduct.promotion_price || this.selectedProduct.price || 0);
        }
        const ret = (this.selectedProduct && this.selectedProduct.promotion_price) || (this.selectedProduct && this.selectedProduct.price) || 0;
        return ret + this.addPrice;
    }
    async created() {
        this.isLoading = true;
        if (!this.rate) {
            await this.getRate();
        }
        // Ưu tiên lấy itemUrl từ prop nếu có
        this.itemUrlLocal = this.itemUrl || (this.$route.query.itemUrl as string) || '';
        if (this.itemUrlLocal) {
            await this.loadProductDetail();
        }
        
        // Set minimum quantity for 1688 products
        // if (this.selectedProduct && this.selectedProduct.moq && this.selectedProduct.moq > 1) {
        //     this.selectedQuantity = this.selectedProduct.moq;
        // }
        
        this.isLoading = false;
    }

    private async loadProductDetail() {
        const response = await cartServices.detailProduct(this.itemUrlLocal);
        if (response && response.status === 200 && response.data && response.data.data && response.data.data.success) {
            this.selectedProduct = response.data.data; // API response structure from Elim API
            if (this.selectedProduct.img_urls && this.selectedProduct.img_urls.length > 0) {
                this.mainImage = this.selectedProduct.img_urls[0];
            } else {
                this.mainImage = ''; // Fallback for no images
            }

            // Initialize isActive for option values - this will be called after availableOptions is computed
            this.$nextTick(() => {
                if (this.availableOptions) {
                    this.availableOptions.forEach(optionGroup => {
                        if (optionGroup.values) {
                            optionGroup.values.forEach(value => {
                                this.$set(value, 'isActive', false); // Use $set for reactivity
                            });
                        }
                    });
                }
            });
        } else {
            Vue.notify({
                group: 'foo',
                type: 'error',
                title: 'Thông báo!',
                text: 'Không thể tải chi tiết sản phẩm. Vui lòng cài đặt extension để lên đơn.',
            });
            this.selectedProduct = null;
        }
    }

    private changeMainImage(image) {
        this.mainImage = image;
    }

    private handleImageError(event) {
        // Fallback khi ảnh không load được
        const img = event.target;
        // Sử dụng ảnh placeholder hoặc ảnh mặc định
        img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkPDtCDhuJdhbmggYeG6v24gY+G6q3UgdOG6jWkg4bm6YW5oPC90ZXh0Pgo8L3N2Zz4K';
    }
    
    private selectAttribute(optionGroup, selectedValue) {
        
        // Use Vue.set to ensure reactivity for each value
        optionGroup.values.forEach((value, index) => {
            const newActiveState = value === selectedValue;
            
            // Use $set with the full path to ensure Vue tracks the change
            this.$set(optionGroup.values[index], 'isActive', newActiveState);
        });


        // Force Vue to re-evaluate computed properties by updating a reactive property
        // We'll add a temporary reactive counter to force updates
        if (!this.reactivityCounter) {
            this.$set(this, 'reactivityCounter', 0);
        }
        this.reactivityCounter++;

        // Update main image if the selected option has an associated image
        const selectedSku = this.getSelectedSkuBasedOnOptions();
        if (selectedSku && selectedSku.img_url) {
            this.mainImage = selectedSku.img_url;
        }
        
        
        // Force reactivity update for computed properties
        this.$nextTick(() => {
            this.$forceUpdate();
        });
    }

    private getSelectedSku() {
        const selectedOptions: string[] = [];
        
        // Use the computed selectedOptions for consistency
        for (const [optionName, optionValue] of Object.entries(this.selectedOptions)) {
            selectedOptions.push(`${optionName}: ${optionValue}`);
        }
        
        return selectedOptions;
    }

    private increaseQuantity() {
        this.selectedQuantity++;
        this.validateQuantity();
        // Force update giá khi thay đổi số lượng (đặc biệt cho 1688)
        if (this.selectedProduct && this.selectedProduct.quote_type === 'by_volume') {
            this.$forceUpdate();
        }
    }

    private decreaseQuantity() {
        if (this.selectedQuantity > 1) {
            this.selectedQuantity--;
        }
        this.validateQuantity();
        // Force update giá khi thay đổi số lượng (đặc biệt cho 1688)
        if (this.selectedProduct && this.selectedProduct.quote_type === 'by_volume') {
            this.$forceUpdate();
        }
    }
    
    private validateQuantity() {
        if (this.selectedQuantity < 1 || isNaN(this.selectedQuantity)) {
            this.selectedQuantity = 1;
        }
        
        // Check minimum order quantity for 1688
        // if (this.selectedProduct && this.selectedProduct.moq && this.selectedQuantity < this.selectedProduct.moq) {
        //     this.selectedQuantity = this.selectedProduct.moq;
        // }
        
        // Force update giá khi thay đổi số lượng (đặc biệt cho 1688)
        if (this.selectedProduct && this.selectedProduct.quote_type === 'by_volume') {
            this.$forceUpdate();
        }
    }

    private getSiteName(productData) {
        // Determine site based on quote_type and other indicators
        if (!productData) { return ''; }
        
        if (productData.platform === '1688') {
            return '1688';
        } else if (productData.platform === 'taobao') {
            return 'TAOBAO';
        } else if (productData.platform === 'tmall') {
            return 'TMALL';
        }
        
        return '';
    }

    private async getRate() {
        const result = await commonService.exchangeRate();
        if (result && result.status === 200 && result.data && result.data.data) {
            (window['getApp'] as any).$emit('SET_RATE', result.data.data.exchange_rate);
            (window['getApp'] as any).$emit('SET_MIN_FEEBUY', result.data.data.min_feebuy);
        }
    }

    // getPriceByQuantity is effectively replaced by the logic within currentPrice computed property
    // If specific quantity-based pricing were to be re-introduced, this method would need new logic.
    // For now, currentPrice handles SKU-based price variations.

    private async addToCart() {
        if (!this.selectedProduct) {
            Vue.notify({ group: 'foo', type: 'error', title: 'Thông báo!', text: 'Sản phẩm không tồn tại.' });
            return;
        }
        
        const selectedSkuOptions = this.getSelectedSku();
        const hasSkus = this.selectedProduct.skus && this.selectedProduct.skus.length > 0;
        const hasMultipleOptions = this.availableOptions && this.availableOptions.length > 0;
        
        // Use computed selectedOptions for better accuracy
        const selectedOptionsCount = Object.keys(this.selectedOptions).length;
        
        if (hasMultipleOptions && selectedOptionsCount < this.availableOptions.length) {
            Vue.notify({
                group: 'foo',
                type: 'error',
                title: 'Thông báo!',
                text: 'Vui lòng chọn đầy đủ thuộc tính sản phẩm trước khi thêm vào giỏ hàng',
            });
            return;
        }

        if (this.currentPrice <= 0) {
            Vue.notify({
                group: 'foo',
                type: 'error',
                title: 'Thông báo!',
                text: 'Giá sản phẩm không hợp lệ. Vui lòng chọn lại thuộc tính.'
            });
            return;
        }

        this.isLoading = true;
        const cartItem = {
            'ShopID': this.selectedProduct.shop_id,
            'ShopName': this.selectedProduct.shop_name || '',
            'Site': this.getSiteName(this.selectedProduct),
            'Note': '',
            'IsFragileGoods': 0,
            'IsFeeShip': 0,
            'Products': [
                {
                    'item_id': this.selectedProduct.id,
                    'shop_id': this.selectedProduct.shop_id,
                    'shop_name': this.selectedProduct.shop_name || '',
                    'title_origin': this.selectedProduct.title,
                    'image_origin': this.mainImage,
                    'link_origin': this.itemUrlLocal, // Use the original URL provided
                    'property': selectedSkuOptions.join('; '),
                    'quantity': this.selectedQuantity,
                    'price_origin': this.currentPrice,
                    'price_promotion': this.currentPrice,
                    'comment': ''
                }
            ]
        };
        
        const result = await cartServices.cartAddItem(cartItem);
        this.isLoading = false;

        if (result && result.data.status === 200 && result.data.message === 'Success') {
            Vue.notify({
                group: 'foo',
                type: 'success',
                title: 'Thông báo!',
                text: 'Đã thêm sản phẩm vào giỏ hàng. Bạn hãy nhấn vào Giỏ hàng để lên đơn nhé.',
            });
        } else if (result && result.data.status === 202) {
            Vue.notify({
                group: 'foo',
                type: 'error',
                title: 'Thông báo!',
                text: result.data.error,
                duration: 5000
            });
        } else {
            Vue.notify({
                group: 'foo',
                type: 'error',
                title: 'Thông báo!',
                text: (result && result.data && result.data.message) || 'Có lỗi trong quá trình thêm vào giỏ hàng, vui lòng liên hệ quản trị!',
            });
        }
    }
}
